import { C } from '../../../../shared/constants/dashboardConstants';

/**
 * 평가 기준 색. 기존 대시보드 색상 토큰에서 고른다.
 * 기준은 C1, C2… 순서로 오므로 순서대로 같은 색을 준다 — 같은 배역 안에서는 늘 같은 색이다.
 */
export const CRITERION_COLORS = [C.blue, C.green, C.orange, C.purple, C.point];

export const colorForCriterion = (index) => CRITERION_COLORS[index % CRITERION_COLORS.length];

/** 계산이 아직 끝나지 않은 상태 — 이 상태가 하나라도 있으면 주기적으로 다시 읽는다 */
export const BUSY_STATUSES = ['PENDING', 'RUNNING'];
export const isBusy = (status) => BUSY_STATUSES.includes(status);

/**
 * 기준 하나가 총점(0~100)에 보탠 몫.
 * 서버 총점 = 가중 평균(1~5)을 0~100으로 옮긴 값 = Σ weight × (score − 1) / 4.
 * 그래서 기준별 몫을 모두 더하면 총점이 되고, 막대 하나를 100점 길이로 나눠 그릴 수 있다.
 */
export function contribution(score, weight) {
  if (score == null || weight == null) return 0;
  const clamped = Math.min(5, Math.max(1, Number(score)));
  return (Number(weight) * (clamped - 1)) / 4;
}

/** 만 나이. 서버가 age를 주면 그 값을, 아니면 생년월일로 생일이 지났는지까지 따져 계산한다. */
export function ageOf(item, today = new Date()) {
  if (item.age != null && item.age !== '') return Number(item.age);
  if (!item.birthDate) return null;
  // 'YYYY-MM-DD'를 그대로 쪼갠다 — new Date('YYYY-MM-DD')는 UTC 자정이라 시간대에 따라 하루 밀린다
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(item.birthDate));
  if (!m) return null;
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  let age = today.getFullYear() - year;
  const beforeBirthday = today.getMonth() + 1 < month || (today.getMonth() + 1 === month && today.getDate() < day);
  if (beforeBirthday) age -= 1;
  return age;
}

function photoOf(item) {
  const medias = item.medias || [];
  const closeUp = medias.find(m => m.photoType === 'PROFILE_CLOSE_UP' && (m.url || m.fileUrl));
  return closeUp?.url ?? closeUp?.fileUrl ?? item.profileImageUrl ?? item.profileImage ?? null;
}

/**
 * 서버 항목(배우 카드 DTO + rank·total·scores·reason) → 화면 모델.
 * index는 화면에 보일 목록 안의 위치(0부터) — 거른 뒤의 순서를 넘긴다.
 * 배우 정보가 평평하게 오든 talent/talentProfile 안에 묶여 오든 받는다.
 */
export function mapRecommendedActor(item, index = 0) {
  const talent = item.talent ?? item.talentProfile ?? item;
  const id = item.talentProfileId ?? talent.talentProfileId ?? talent.id ?? null;
  const age = ageOf(talent);
  const heightCm = talent.heightCm ?? talent.height ?? null;
  return {
    id,
    slug: talent.slug ?? null,
    name: talent.stageName || talent.name || '—',
    photoUrl: photoOf(talent),
    gender: talent.gender ?? null,
    age,
    heightCm,
    weightKg: talent.weightKg ?? null,
    skills: (talent.skills || []).map(s => s.name || s.skillName || s).filter(Boolean),
    // 화면 순위는 목록 순서로 매긴다 — 서버가 읽을 때 동의·성인 여부로 다시 거르면 rank에 빈 번호가 생긴다
    rank: index + 1,
    serverRank: item.rank ?? null,
    total: item.total ?? null,
    scores: item.scores || {},
    reason: item.reason || '',
  };
}

/** "41세 · 183cm" — 없는 값은 빼고 잇는다 */
export function actorMeta(actor) {
  return [actor.age != null ? `${actor.age}세` : null, actor.heightCm ? `${actor.heightCm}cm` : null]
    .filter(Boolean)
    .join(' · ') || '—';
}

/** "3분 전" 같은 짧은 상대 시간 */
export function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;
  const sec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (sec < 60) return '방금';
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hour = Math.round(min / 60);
  if (hour < 24) return `${hour}시간 전`;
  return `${Math.round(hour / 24)}일 전`;
}
